import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Icon, MenuItem } from "@material-ui/core";
import _ from "lodash";

import MediumEditor from "medium-editor";
import "medium-editor/dist/css/medium-editor.min.css";
import "medium-editor/dist/css/themes/default.min.css";

import getLogger, { LogGroup } from "js/core/logger";
import { stopPropagation } from "js/core/utilities/stopPropagation";
import { handleEditSharedSlide } from "js/editor/editShareSlide";

import { sanitizeHtml } from "js/core/utilities/dompurify";
import { Key } from "js/core/utilities/keys";

import { GenerateSlideNoteType } from "common/aiConstants";
import { ActionPermissionsObject } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import { generateSlideNotes } from "js/core/utilities/generateSlideNotes";
import { ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ProgressDialog from "js/react/components/Dialogs/ProgressDialog";
import { PopupMenu } from "js/react/components/PopupMenu";
import PresentationEditorController from "../PresentationEditorController";
import AIIcon from "js/react/components/AIIcon";
import { Divider } from "js/Components/Divider";

const logger = getLogger(LogGroup.EDITOR);

const Container = styled.div`
    overflow: unset;
    z-index: 1000;

    width: 100%;
    height: 100%;
    background: #333;
    font-size: 14px;
    cursor: default;
    max-width: 100vw;
    max-height: 100vh;
`;

const SpeakerNotesHeader = styled.div`
    height: 45px;
    color: white;
    padding: 0px 20px;
    background: #333;
    text-align: center;
    pointer-events: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    //position: relative;
`;

const SpeakerNotesTitle = styled.div`
    color: white;
    font-size: 15px;
    letter-spacing: .9px;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 20px;
`;

const SpeakerNotesCloseButton = styled.div`
    color: #ccc;
    pointer-events: auto;
    cursor: pointer;
    text-align: center;
    z-index: 100;
    display: flex;
`;

const Contents = styled.div`
    background: #f1f1f1;
    clear: both;
    position: relative;
    overflow: auto;
    height: calc(100% - 40px);
`;

const SpeakerNotesEditorContainer = styled.div`
    overflow: auto;
    width: 100%;
    height: calc(100% - 6px);
    resize: none;
    font-size: 14px;
    color: #333;
    padding: 10px 15px;
    box-sizing: border-box;
    border: solid 1px white;
    background: white;
    border-radius: 0;
    cursor: text;

    &:focus {
      border: solid 1px #eee;
      outline: none;
    }

    &.disabled {
      pointer-events: none;
    }
`;

const PopupMenuContainer = styled.div`
    display: block;
    color: white;
    z-index: 100;
`;

const StyledPopupMenu = styled(PopupMenu)`
    > button {
        padding: 3px 2px;

        :hover {
            background: #4B4E55;
        }
    }
`;

const MediumEditorToolbarContainer = styled.div`
    z-index: 1000;
    pointer-events: all;

    .medium-editor-toolbar {
        position: relative;
        border-radius: 0px !important;
        box-shadow: none !important;
        background: none !important;
        border: none !important;;
    }

    .medium-editor-toolbar li button {
        min-width: 35px !important;
        height: 45px !important;
        padding: 0px !important;
        border: none !important;
        background: none !important;
        box-shadow: none !important;

        &:hover{
            color: #29B2E5 !important;
            background-color: #333 !important;
        }
    }

    .medium-editor-button-active {
        color: #29B2E5 !important;
        background-color: #333 !important;
    }

    .medium-editor-action {
        display: flex !important;
        align-items: center;
        justify-content: center;

        i {
            font-size: 18px;
        }
    }
`;

const StyledCloseIcon = styled(Icon)`
  &&& {
    align-self: center;
  }
`;

const SpeakerNotes = ({ currentSlide, slides, transitionState, isSingleSlideEditor, isResizing, currentCanvasController, onClose }) => {
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [shouldAutoFocus, setShouldAutoFocus] = useState(true);

    const mediumEditorToolbarContainerRef = useRef(null);
    const mediumEditorContainerRef = useRef(null);
    const mediumEditorRef = useRef(null);
    const saveSlideTimeout = useRef(null);
    const popupRef = useRef(null);

    const isCurrentSlideReadOnly = currentSlide && !isSingleSlideEditor && currentSlide.isLibrarySlide();

    const shouldShowDesignerBot = WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use;

    useEffect(() => {
        if (transitionState !== "entered") {
            setShouldAutoFocus(true);
            return;
        }

        mediumEditorRef.current?.destroy();

        if (isResizing) {
            return;
        }

        setupMediumEditor();
        renderContents();

        return () => {
            savePendingTextImmediately();

            mediumEditorRef.current?.destroy();
        };
    }, [currentSlide, transitionState, isResizing]);

    useEffect(() => {
        const positionPopup = () => {
            const popup = popupRef.current;
            const editorContainer = mediumEditorContainerRef.current;

            if (popup) {
                const editorRect = editorContainer.getBoundingClientRect();
                const middleX = editorRect.width / 2;
                popup.style.left = `${middleX + 115}px`;
            }
        };

        // Initial positioning
        positionPopup();

        // Recalculate position on window resize
        window.addEventListener("resize", positionPopup);

        if (mediumEditorContainerRef.current) {
            mediumEditorContainerRef.current.addEventListener("focus", positionPopup);
        }

        // Cleanup event listener on unmount
        return () => {
            window.removeEventListener("resize", positionPopup);
            if (mediumEditorContainerRef.current) {
                mediumEditorContainerRef.current.removeEventListener("focus", positionPopup);
            }
        };
    }, [transitionState, isResizing, currentSlide]);

    const setupMediumEditor = () => {
        mediumEditorRef.current = new MediumEditor(mediumEditorContainerRef.current, {
            toolbar: {
                buttons: [
                    "bold",
                    "italic",
                    "underline",
                    "strikethrough",
                    {
                        name: "unorderedlist",
                        classList: ["norderedlist"]
                    },
                    {
                        name: "orderedlist",
                        classList: ["orderedlist"]
                    }
                ],
                static: true,
                sticky: true,
                updateOnEmptySelection: true,
                relativeContainer: mediumEditorToolbarContainerRef.current
            },
            paste: {
                cleanPastedHTML: true
            },
            placeholder: {
                text: "Type your speaker notes",
                hideOnClick: true
            },
            disableEditing: isCurrentSlideReadOnly
        });
    };

    const scheduleSaveText = () => {
        if (saveSlideTimeout.current) {
            clearTimeout(saveSlideTimeout.current);
        }

        saveSlideTimeout.current = setTimeout(() => {
            saveText();
            saveSlideTimeout.current = null;
        }, 5000);
    };

    const savePendingTextImmediately = () => {
        if (saveSlideTimeout.current) {
            clearTimeout(saveSlideTimeout.current);
            saveSlideTimeout.current = null;

            saveText();
        }
    };

    const saveText = () => {
        currentSlide.update({
            slide_notes: sanitizeHtml(mediumEditorRef.current.getContent(0))
        });
    };

    const subscribeToEvents = () => {
        if (!mediumEditorRef.current) {
            return;
        }

        mediumEditorRef.current.subscribe("editableInput", () => {
            scheduleSaveText();
        });

        mediumEditorRef.current.subscribe("focus", event => {
            event.preventDefault();
            currentCanvasController.lockSlideForCollaborators(120);
            setIsPopupVisible(true);
        });

        mediumEditorRef.current.subscribe("blur", event => {
            handleBlur(event);
        });
    };

    const handleBlur = event => {
        const relatedTarget = event.relatedTarget;
        const toolbar = document.querySelector(".medium-editor-element");

        if ((popupRef.current && popupRef.current.contains(relatedTarget)) || (toolbar && toolbar.contains(relatedTarget))) {
            // Clicked inside the popup, do not trigger blur logic
            return;
        }

        savePendingTextImmediately();
        setIsPopupVisible(false);

        if (currentCanvasController.isLockedForCollaborators()) {
            currentCanvasController.unlockSlideForCollaborators();
        }
    };

    useEffect(() => {
        setIsPopupVisible(false);
    }, [currentSlide]);

    const renderContents = () => {
        const slideId = currentSlide.id;

        currentSlide.load()
            .then(() => {
                if (!mediumEditorRef.current || currentSlide?.id !== slideId) {
                    // Slide changed or closed, no need to proceed
                    return;
                }

                mediumEditorRef.current.setup();
                mediumEditorRef.current.setContent(sanitizeHtml(currentSlide.get("slide_notes") || ""), 0);

                if (!isCurrentSlideReadOnly) {
                    subscribeToEvents();

                    document.getElementsByClassName("norderedlist")[0].innerHTML = '<i class="micon">format_list_bulleted</i>';
                    document.getElementsByClassName("orderedlist")[0].innerHTML = '<i class="micon">format_list_numbered</i>';

                    if (!shouldAutoFocus) {
                        return;
                    }

                    _.defer(() => {
                        mediumEditorRef.current.elements[0].focus();
                        setShouldAutoFocus(false);
                    });
                }
            })
            .catch(err => logger.error(err, "renderContents() failed"));
    };

    const handleKeyDown = event => {
        if (event.keyCode === Key.TAB) {
            event.preventDefault();
        }
    };

    const getPreviousSlide = () => {
        const index = slides.findIndex(slide => slide.id === currentSlide.id);
        if (index > 0) {
            return slides[index - 1];
        } else {
            return null;
        }
    };

    const getNextSlide = () => {
        const index = slides.findIndex(slide => slide.id === currentSlide.id);
        if (index < slides.length - 1) {
            return slides[index + 1];
        } else {
            return null;
        }
    };

    const applyGenerateSlideNotesResult = async (result, isRewrite = false) => {
        let { slideNotes } = result;

        slideNotes = slideNotes.replace(/\n/g, "<br/>");

        if (slideNotes.startsWith("•")) {
            slideNotes = slideNotes.replace(/•/g, "<br/>•");
            if (slideNotes.startsWith("<br/>•")) {
                slideNotes = slideNotes.substring(5);
            }
        }

        slideNotes = sanitizeHtml(slideNotes);
        const editor = mediumEditorRef.current;
        const oldContent = editor.getContent(0);
        let newContent;

        if (isRewrite) {
            newContent = slideNotes;
        } else {
            newContent = oldContent && oldContent.trim()
                ? oldContent + "<br/><br/>" + slideNotes
                : slideNotes;
        }

        editor.setContent(newContent, 0);

        if (!currentSlide.isDetached) {
            return;
        }

        await currentSlide.update(
            { slide_notes: newContent },
            { removeMissingKeys: true, silent: true, save: false }
        );

        await currentSlide.saveChanges();
    };

    const generateNotes = async notesType => {
        const dialogProgress = ShowDialog(ProgressDialog, {
            title: "Generating notes...",
        });

        setIsPopupVisible(false);

        const handleError = error => {
            logger.error(error, "Error while performing slide notes generate");
            dialogProgress.props.closeDialog();
            ShowErrorDialog({
                title: (
                    <>Unfortunately, the notes generation failed.<br />Please try again.</>
                ),
            });
        };

        const prevSlideText = getPreviousSlide()?.getSlideText();
        const nextSlideText = getNextSlide()?.getSlideText();
        const prevSlideNotes = getPreviousSlide()?.get("slide_notes");

        await generateSlideNotes({
            notesType,
            slideText: currentSlide.getSlideText(),
            slideIndex: PresentationEditorController.getCurrentSlideIndex() + 1,
            prevSlideText,
            nextSlideText,
            prevSlideNotes,
            onReportState: async ({
                results,
                isGenerating,
                error,
            }) => {
                if (error) {
                    handleError(error);
                } else if (
                    !isGenerating &&
                    results.length
                ) {
                    const result = results[0];
                    if (!result?.slideNotes?.length) {
                        handleError("Result text is empty.");
                        return;
                    }

                    await applyGenerateSlideNotesResult(result);
                    dialogProgress.props.closeDialog();
                }
            }
        });
    };

    const rewriteSlideNotes = async () => {
        savePendingTextImmediately();

        const notesText = currentSlide.get("slide_notes");
        const prevSlideText = getPreviousSlide()?.getSlideText();
        const nextSlideText = getNextSlide()?.getSlideText();
        const prevSlideNotes = getPreviousSlide()?.get("slide_notes");
        if (!notesText) {
            ShowErrorDialog({
                title: (
                    <>There are no notes to rewrite.<br />Please generate new notes or add them manually.</>
                ),
            });
            return;
        }

        const dialogProgress = ShowDialog(ProgressDialog, {
            title: "Generating notes...",
        });

        const handleError = error => {
            logger.error(error, "Error while performing slide notes generate");
            dialogProgress.props.closeDialog();
            ShowErrorDialog({
                title: (
                    <>Unfortunately, the notes generation failed.<br />Please try again.</>
                ),
            });
        };

        const notesType = GenerateSlideNoteType.REWRITE;

        await generateSlideNotes({
            notesType,
            slideText: currentSlide.getSlideText(),
            notesText,
            slideIndex: PresentationEditorController.getCurrentSlideIndex() + 1,
            prevSlideText,
            nextSlideText,
            prevSlideNotes,
            onReportState: async ({
                results,
                isGenerating,
                error,
            }) => {
                if (error) {
                    handleError(error);
                } else if (
                    !isGenerating &&
                    results.length
                ) {
                    const result = results[0];
                    if (!result?.slideNotes?.length) {
                        handleError("Result text is empty.");
                    }

                    await applyGenerateSlideNotesResult(result, true);
                    dialogProgress.props.closeDialog();
                }
            }
        });
    };

    return (<Container>
        <SpeakerNotesHeader>
            <SpeakerNotesTitle>Speaker Notes</SpeakerNotesTitle>
            <div style={{ display: "flex", alignItems: "center" }}>
                <MediumEditorToolbarContainer ref={mediumEditorToolbarContainerRef} />
                {shouldShowDesignerBot && (
                    <PopupMenuContainer
                        title="AI writing assistant"
                        ref={popupRef}
                        style={{ display: isPopupVisible ? "block" : "none" }}
                    >
                        <StyledPopupMenu
                            data-test-id="generate-notes"
                            component={<AIIcon style={{ width: 18, height: 18 }} />}
                            childrenAreMenuItems
                            disableOverflow
                        >
                            <MenuItem
                                onClick={() => generateNotes(GenerateSlideNoteType.SCRIPT)}
                            >
                                <Icon>comment</Icon>
                                <span>Generate script</span>
                            </MenuItem>
                            <MenuItem
                                onClick={() => generateNotes(GenerateSlideNoteType.BULLET_POINTS)}
                            >
                                <Icon>format_list_bulleted</Icon>
                                <span>Generate bullet points</span>
                            </MenuItem>
                            <Divider color="#D9D9D9" margin={0} />
                            <MenuItem
                                onClick={() => rewriteSlideNotes()}
                            >
                                <Icon>autorenew</Icon>
                                <span>Rewrite my notes</span>
                            </MenuItem>
                        </StyledPopupMenu>
                    </PopupMenuContainer>
                )}
            </div>
            <SpeakerNotesCloseButton onClick={onClose}>
                <StyledCloseIcon fontSize="small">close</StyledCloseIcon>
            </SpeakerNotesCloseButton>
        </SpeakerNotesHeader>
        <Contents>
            <SpeakerNotesEditorContainer
                onScroll={stopPropagation()}
                onKeyDown={stopPropagation(handleKeyDown)}
                onPaste={stopPropagation()}
                onClick={() => isCurrentSlideReadOnly && handleEditSharedSlide()}
                ref={mediumEditorContainerRef}
            />
        </Contents>
    </Container>);
};

export default SpeakerNotes;
